import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';

import { NestedTreeControl } from '@angular/cdk/tree';
import { FormControl } from '@angular/forms';
import { MatPaginator } from '@angular/material/paginator';
import { MatTreeNestedDataSource } from '@angular/material/tree';
import { Actions, Select, Store } from '@ngxs/store';
import { AllUserDataSource } from 'app/library/all-users/components/active-user-list/datasource';
import { TeamsState } from 'app/library/team/models/teams.state';
import { UserTeamRoles } from 'app/library/team/models/user-team-roles';
import { TeamService } from 'app/library/team/services/team.service';
import { UnsubscribeOnDestroy } from 'app/projects/core/src/lib/models/unsubscribe-on-destroy';
import { IEntityNode } from 'app/projects/shared/src/lib/interfaces/entity-node.interface';
import { User } from 'app/projects/user/src/lib/models/user';
import { Observable } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-add-user-to-team-dialog',
    templateUrl: './add-user-to-team-dialog.component.html',
    styleUrls: ['./add-user-to-team-dialog.component.scss'],
})
export class AddUserToTeamDialogComponent extends UnsubscribeOnDestroy implements OnInit {
    @Select(TeamsState.isFetchingList)
    isFetching$: Observable<boolean>;

    @Select(TeamsState.getTotalCount)
    totalCount$: Observable<number>;

    @ViewChild(MatPaginator)
    paginator: MatPaginator;

    @Input()
    listTree: IEntityNode[];

    @Input()
    entities$: Observable<User[]>;

    @Input()
    title: string;

    @Input()
    teamId: string;

    @Input()
    role: string;

    @Input()
    assignAsTrainingResponsible = false;

    treeControl = new NestedTreeControl<IEntityNode>((node) => node.children);
    dataSource = new MatTreeNestedDataSource<IEntityNode>();
    teamsUsersDataSource: AllUserDataSource;
    searchInput = new FormControl();

    displayedColumns: string[] = ['avatar', 'name', 'role', 'email', 'buttons'];
    teamsUsersDisplayedColumns: string[] = ['avatar', 'firstName', 'lastName', 'email', 'buttons'];
    selectedUsersSet: Set<User> = new Set<User>();

    UserTeamRoles = UserTeamRoles;

    constructor(public dialogRef: MatDialogRef<AddUserToTeamDialogComponent>, private _teamService: TeamService, private _store: Store, private _action$: Actions) {
        super();
    }

    ngOnInit(): void {
        this.dataSource.data = this.listTree;

        this.searchInput.valueChanges.pipe(takeUntil(this._unsubscribeAll), debounceTime(300), distinctUntilChanged()).subscribe(() => {
            this.loadUsersPage(true);
        });

        this.teamsUsersDataSource = new AllUserDataSource(this.entities$, this._store, this._action$);

        setTimeout(() => {
            this.paginator.page.pipe(takeUntil(this._unsubscribeAll)).subscribe(() => {
                this.loadUsersPage(false);
            });
        });
    }

    hasChild = (_: number, node: IEntityNode) => !!node.children && node.children.length > 0;

    onSelect(user: User): void {
        this.selectedUsersSet.add(user);
    }

    onDeselect(user: User): void {
        const userToRemove = [...this.selectedUsersSet].find((selectedUser) => selectedUser.id === user.id);

        if (userToRemove) {
            this.selectedUsersSet.delete(userToRemove);
        }
    }

    async getTeamUsers(node: IEntityNode): Promise<void> {
        node.isLoading = true;
        node.users = await this._teamService.fetchTeamUsers(node.id, this.teamId, this.role);
        node.isLoading = false;
    }

    onClearSelection(): void {
        this.selectedUsersSet.clear();
    }

    async loadUsersPage(resetPageIndex = false): Promise<void> {
        if (resetPageIndex) {
            this.paginator.pageIndex = 0;
        }

        if (this.teamsUsersDataSource) {
            this.teamsUsersDataSource.loadUsers(
                'teamsUsers',
                [],
                this.teamId,
                this.searchInput.value,
                '',
                '',
                this.paginator.pageSize,
                this.paginator.pageIndex + 1,
                false,
                this.role
            );
        }
    }

    isUserSelected(user: User): boolean {
        return [...this.selectedUsersSet].some((selectedUser) => selectedUser.id === user.id);
    }

    onSave(): void {
        this.dialogRef.close({
            selectedNodes: this.selectedUsersSet,
            assignAsTrainingResponsible: this.assignAsTrainingResponsible,
        });
    }
}
